import type { NextRequest } from 'next/server'
import { NextResponse } from 'next/server'
import { randomUUID } from 'crypto'
import { and, eq } from 'drizzle-orm'
import { db } from '@/db'
import { users, sessions, tenantMemberships } from '@/db/schema'

const PREFERRED_TENANT_COOKIE = 'preferred-tenant-id'

export async function GET(request: NextRequest) {
  if (process.env.NODE_ENV === 'production') {
    return NextResponse.json({ error: 'Not found' }, { status: 404 })
  }

  const email = request.nextUrl.searchParams.get('email')?.toLowerCase().trim()
  const host = request.headers.get('host') ?? 'localhost:3000'
  const base = `http://${host}`

  if (!email) {
    return NextResponse.redirect(new URL('/login', base))
  }

  const [user] = await db
    .select({ id: users.id, displayName: users.displayName })
    .from(users)
    .where(eq(users.email, email))
    .limit(1)

  if (!user) {
    return NextResponse.redirect(new URL('/login?error=user-not-found', base))
  }

  // Ensure user has a display name so they skip the onboarding screen
  if (!user.displayName) {
    await db.update(users).set({ displayName: email.split('@')[0] }).where(eq(users.id, user.id))
  }

  // Resolve an active membership for the session. Prefer expert/admin access
  // so the dev bypass lands on the most useful workspace for seeded users.
  const memberships = await db
    .select({ tenantId: tenantMemberships.tenantId, role: tenantMemberships.role })
    .from(tenantMemberships)
    .where(and(eq(tenantMemberships.userId, user.id), eq(tenantMemberships.isActive, true)))

  const membership =
    memberships.find((item) => item.role === 'expert' || item.role === 'admin') ??
    memberships[0]

  if (!membership) {
    return NextResponse.redirect(new URL('/login?error=user-not-found', base))
  }

  const sessionToken = randomUUID()
  const expires = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)

  await db.insert(sessions).values({ sessionToken, userId: user.id, expires })

  const role = membership?.role
  const dest = role === 'expert' || role === 'admin' ? '/inbox' : '/submit'
  const response = NextResponse.redirect(new URL(dest, base))
  response.cookies.set('authjs.session-token', sessionToken, {
    expires,
    httpOnly: true,
    sameSite: 'lax',
    path: '/',
  })
  response.cookies.set(PREFERRED_TENANT_COOKIE, membership.tenantId, {
    expires,
    httpOnly: true,
    sameSite: 'lax',
    path: '/',
  })

  return response
}
